import { z } from 'zod';

/**
 * Loan Intake DTOs
 *
 * Zod schemas for loan intake wizard data validation.
 *
 * @see Issue #432 - Templated Kiavi-Style Loan Intake Wizard
 */

// =============================================================================
// ADDRESS SCHEMA
// =============================================================================

export const AddressSchema = z.object({
  street1: z.string().min(1, 'Street address is required'),
  street2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2).max(2, 'State must be 2-letter code'),
  postalCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code'),
  country: z.string().default('US'),
});

export type Address = z.infer<typeof AddressSchema>;

// =============================================================================
// INTAKE FLOW TYPES
// =============================================================================

export const IntakeFlowTypeSchema = z.enum([
  'bridge',
  'construction',
  'rental',
  'commercial',
  'general',
  'partner_invitation',
  'partner_onboarding',
]);

export type IntakeFlowTypeEnum = z.infer<typeof IntakeFlowTypeSchema>;

// =============================================================================
// INTAKE CATEGORIES
// =============================================================================

/**
 * High-level intake category — auto-derived from intakeType server-side.
 * Used for indexed filtering in the business portal (e.g. "show all partner apps").
 */
export const IntakeCategorySchema = z.enum(['loan', 'partner']);
export type IntakeCategory = z.infer<typeof IntakeCategorySchema>;

/**
 * Maps each IntakeFlowType to its parent category.
 * Used by the CRM service to auto-populate intakeCategory on submission.
 */
export const INTAKE_TYPE_TO_CATEGORY: Record<IntakeFlowTypeEnum, IntakeCategory> = {
  bridge: 'loan',
  construction: 'loan',
  rental: 'loan',
  commercial: 'loan',
  general: 'loan',
  partner_invitation: 'partner',
  partner_onboarding: 'partner',
};

/**
 * Reverse mapping: category → list of IntakeFlowType values.
 * Used by the business portal to filter submissions by category.
 */
export const INTAKE_TYPES_BY_CATEGORY: Record<IntakeCategory, IntakeFlowTypeEnum[]> = {
  loan: ['bridge', 'construction', 'rental', 'commercial', 'general'],
  partner: ['partner_invitation', 'partner_onboarding'],
};

// =============================================================================
// INTAKE URL PARAMS - Attribution tracking for intake wizard links
// =============================================================================

/**
 * URL query parameter names for loan intake wizard
 * Used for attribution tracking: who created the link, which template, marketing source
 */
export const IntakeUrlParams = {
  /** Business ID - identifies which business created/shared the link (multi-tenancy) */
  BUSINESS_ID: 'b',
  /** Alternate business ID param (for backwards compatibility) */
  BUSINESS_ID_ALT: 'businessId',
  /** Template ID - specific template to load */
  TEMPLATE_ID: 'templateId',
  /** Template type - category of template (bridge, construction, etc.) */
  TYPE: 'type',
  /** Partner referral code */
  REF: 'ref',
  /** Alternate referral param */
  REFERRAL: 'referral',
  /** UTM source - marketing attribution */
  UTM_SOURCE: 'utm_source',
  /** UTM medium - marketing attribution */
  UTM_MEDIUM: 'utm_medium',
  /** UTM campaign - marketing attribution */
  UTM_CAMPAIGN: 'utm_campaign',
  /** Pre-fill token - for lead conversion or partner referral pre-fill (Issue #475) */
  PREFILL_TOKEN: 'prefill',
  /** Lead ID - for lead conversion tracking (Issue #475) */
  LEAD_ID: 'leadId',
} as const;

export type IntakeUrlParamKey = keyof typeof IntakeUrlParams;
export type IntakeUrlParamValue = (typeof IntakeUrlParams)[IntakeUrlParamKey];

// =============================================================================
// ENTITY TYPE SCHEMA
// =============================================================================

export const EntityTypeSchema = z.enum(['individual', 'llc', 'corporation', 'trust', 'partnership']);

export type EntityType = z.infer<typeof EntityTypeSchema>;

// =============================================================================
// PROPERTY TYPE SCHEMA
// =============================================================================

export const PropertyTypeSchema = z.enum([
  'sfr',
  'condo',
  'townhouse',
  '2-4_unit',
  'multifamily',
  'mixed_use',
  'commercial',
  'office',
  'retail',
  'industrial',
  'hospitality',
  'self_storage',
  'other',
]);

export type PropertyType = z.infer<typeof PropertyTypeSchema>;

// =============================================================================
// SSN VALIDATION
// =============================================================================

export const SsnSchema = z
  .string()
  .regex(/^\d{9}$/, 'SSN must be exactly 9 digits')
  .transform((val) => val.replace(/\D/g, ''));

export const SsnLast4Schema = z.string().regex(/^\d{4}$/, 'Last 4 of SSN must be exactly 4 digits');

// =============================================================================
// STEP 1: INVESTMENT TYPE
// =============================================================================

export const InvestmentTypeStepSchema = z.object({
  investmentType: IntakeFlowTypeSchema,
});

export type InvestmentTypeStep = z.infer<typeof InvestmentTypeStepSchema>;

// =============================================================================
// STEP 2: ENTITY & EXPERIENCE
// =============================================================================

export const EntityExperienceStepSchema = z.object({
  entityType: EntityTypeSchema,
  entityName: z.string().optional(),
  flipsInLast2Years: z.enum(['0', '1-3', '4-10', '11+']),
  hasGuarantor: z.enum(['yes', 'no']),
});

export type EntityExperienceStep = z.infer<typeof EntityExperienceStepSchema>;

// =============================================================================
// STEP 3: PROPERTY ADDRESS
// =============================================================================

export const PropertyAddressStepSchema = z.object({
  propertyAddress: AddressSchema,
  propertyType: PropertyTypeSchema,
  propertyCondition: z.enum(['excellent', 'good', 'fair', 'poor', 'land']),
});

export type PropertyAddressStep = z.infer<typeof PropertyAddressStepSchema>;

// =============================================================================
// STEP 4: RATE CALCULATOR
// =============================================================================

export const RateCalculatorStepSchema = z.object({
  purchasePrice: z.number().positive().min(50000).max(50000000),
  rehabBudget: z.number().nonnegative().max(5000000).optional(),
  afterRepairValue: z.number().positive().min(50000).max(100000000),
  requestedLoanAmount: z.number().positive(),
  estimatedFico: z.enum(['760+', '740-759', '720-739', '700-719', '680-699', '660-679', 'below-660']),
  preferredTerm: z.enum(['6', '9', '12', '18', '24', '60', '120', '180', '240', '300', '360']),
  expectedMonthlyRent: z.number().positive().optional(), // For DSCR
});

export type RateCalculatorStep = z.infer<typeof RateCalculatorStepSchema>;

// =============================================================================
// STEP 5: COMPLIANCE
// =============================================================================

export const ComplianceAnswerSchema = z.object({
  questionId: z.string(),
  answer: z.boolean(),
});

export const ComplianceStepSchema = z.object({
  complianceAnswers: z.array(ComplianceAnswerSchema).min(1),
});

export type ComplianceAnswer = z.infer<typeof ComplianceAnswerSchema>;
export type ComplianceStep = z.infer<typeof ComplianceStepSchema>;

// =============================================================================
// STEP 6: SIGNING DATE
// =============================================================================

export const SigningDateStepSchema = z.object({
  preferredSigningDate: z.string().datetime(),
  signingFlexibility: z.enum(['exact', 'flexible_1_week', 'flexible_2_weeks', 'no_rush']),
  additionalNotes: z.string().optional(),
});

export type SigningDateStep = z.infer<typeof SigningDateStepSchema>;

// =============================================================================
// STEP 7: GUARANTOR DETAILS
// =============================================================================

export const GuarantorDetailsStepSchema = z.object({
  guarantorFirstName: z.string().min(1, 'First name is required'),
  guarantorLastName: z.string().min(1, 'Last name is required'),
  guarantorDob: z.string().datetime(),
  guarantorPhone: z.string().min(10, 'Phone number is required'),
  guarantorEmail: z.string().email('Valid email is required'),
  guarantorAddress: AddressSchema,
});

export type GuarantorDetailsStep = z.infer<typeof GuarantorDetailsStepSchema>;

// =============================================================================
// STEP 8: SSN VERIFICATION
// =============================================================================

export const SsnVerificationStepSchema = z.object({
  ssn: SsnSchema,
  creditAuthConsent: z.literal(true, { message: 'Credit authorization is required' }),
  termsConsent: z.literal(true, { message: 'You must accept the terms and conditions' }),
});

export type SsnVerificationStep = z.infer<typeof SsnVerificationStepSchema>;

// =============================================================================
// GENERAL CONSULTATION (Short form)
// =============================================================================

export const GeneralConsultationSchema = z.object({
  investmentType: z.literal('general'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().min(10, 'Phone number is required'),
  preferredContactMethod: z.enum(['phone', 'email', 'text']),
  bestTimeToContact: z.enum(['morning', 'afternoon', 'evening', 'anytime']),
  projectDescription: z.string().min(1, 'Project description is required'),
  estimatedLoanAmount: z.enum(['under-100k', '100k-250k', '250k-500k', '500k-1m', '1m-5m', 'over-5m', 'not-sure']),
  timeline: z.enum(['asap', '1-month', '1-3-months', '3-6-months', 'just-exploring']),
});

export type GeneralConsultation = z.infer<typeof GeneralConsultationSchema>;

// =============================================================================
// COMPLETE INTAKE FORM DATA
// =============================================================================

export const IntakeFormDataSchema = z.object({
  // Template tracking
  templateId: z.string(),
  templateVersion: z.string(),

  // Step 1
  investmentType: IntakeFlowTypeSchema,

  // Step 2
  entityType: EntityTypeSchema.optional(),
  entityName: z.string().optional(),
  flipsInLast2Years: z.enum(['0', '1-3', '4-10', '11+']).optional(),
  hasGuarantor: z.enum(['yes', 'no']).optional(),

  // Step 3
  propertyAddress: AddressSchema.optional(),
  propertyType: PropertyTypeSchema.optional(),
  propertyCondition: z.enum(['excellent', 'good', 'fair', 'poor', 'land']).optional(),

  // Step 4
  purchasePrice: z.number().optional(),
  rehabBudget: z.number().optional(),
  afterRepairValue: z.number().optional(),
  requestedLoanAmount: z.number().optional(),
  estimatedFico: z.string().optional(),
  preferredTerm: z.string().optional(),
  expectedMonthlyRent: z.number().optional(),

  // Step 5
  complianceAnswers: z.array(ComplianceAnswerSchema).optional(),

  // Step 6
  preferredSigningDate: z.string().optional(),
  signingFlexibility: z.string().optional(),
  additionalNotes: z.string().optional(),

  // Step 7
  guarantorFirstName: z.string().optional(),
  guarantorLastName: z.string().optional(),
  guarantorDob: z.string().optional(),
  guarantorPhone: z.string().optional(),
  guarantorEmail: z.string().optional(),
  guarantorAddress: AddressSchema.optional(),

  // Step 8 - SSN not stored in full, only last 4
  ssnLast4: SsnLast4Schema.optional(),
  creditAuthConsent: z.boolean().optional(),
  termsConsent: z.boolean().optional(),

  // General consultation fields
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  preferredContactMethod: z.enum(['phone', 'email', 'text']).optional(),
  bestTimeToContact: z.string().optional(),
  projectDescription: z.string().optional(),
  estimatedLoanAmount: z.string().optional(),
  timeline: z.string().optional(),
});

export type IntakeFormData = z.infer<typeof IntakeFormDataSchema>;

// =============================================================================
// INTAKE SUBMISSION SCHEMA (API Input)
// =============================================================================

export const IntakeSubmissionSchema = z.object({
  // Template tracking (required)
  templateId: z.string().min(1, 'Template ID is required'),
  templateVersion: z.string().min(1, 'Template version is required'),

  // Intake type (required)
  intakeType: IntakeFlowTypeSchema,

  // Form data (all optional at API level, validated by template)
  formData: IntakeFormDataSchema.omit({ templateId: true, templateVersion: true }),

  // Referral tracking (optional)
  referralCode: z.string().optional(),
  clickId: z.string().optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmTerm: z.string().optional(),
  utmContent: z.string().optional(),
});

export type IntakeSubmission = z.infer<typeof IntakeSubmissionSchema>;

// =============================================================================
// RATE CALCULATION
// =============================================================================

export const RateCalculationInputSchema = z.object({
  purchasePrice: z.number().positive(),
  rehabBudget: z.number().nonnegative().default(0),
  afterRepairValue: z.number().positive(),
  requestedLoanAmount: z.number().positive(),
  estimatedFico: z.string(),
  preferredTerm: z.number().positive(),
  flipsInLast2Years: z.string().optional(),
  maxLTV: z.number().positive().max(100),
  maxLTC: z.number().positive().max(100),
  defaultRate: z.number().positive(),
});

export type RateCalculationInput = z.infer<typeof RateCalculationInputSchema>;

export const RateCalculationResultSchema = z.object({
  estimatedRate: z.number(),
  ltv: z.number(),
  ltc: z.number(),
  monthlyPayment: z.number(),
  totalCost: z.number(),
  isEligible: z.boolean(),
  ineligibilityReason: z.string().optional(),
  adjustments: z.array(
    z.object({
      description: z.string(),
      adjustment: z.number(),
    }),
  ),
});

export type RateCalculationResult = z.infer<typeof RateCalculationResultSchema>;

// =============================================================================
// BUSINESS INTAKE CONFIG
// =============================================================================

export const BusinessIntakeConfigSchema = z.object({
  businessId: z.string().uuid(),
  activeTemplateIds: z.array(z.string()),
  defaultTemplateId: z.string().optional(),
  customRedirectUrl: z.string().url().optional(),
  enableAdvisorRouting: z.boolean().default(true),
  advisorEmail: z.string().email().optional(),
  updatedAt: z.date(),
});

export type BusinessIntakeConfig = z.infer<typeof BusinessIntakeConfigSchema>;

// =============================================================================
// INTAKE INVESTMENT TYPE CONFIGURATION (Issue #526)
// =============================================================================

/**
 * Investment types that can be configured by business admins
 * These are the consumer-facing options shown in intake forms
 */
export const ConsumerInvestmentTypes = ['bridge', 'construction', 'rental', 'commercial'] as const;
export type ConsumerInvestmentType = (typeof ConsumerInvestmentTypes)[number];

/**
 * Schema for validating investment type configuration
 * Used when updating business settings
 */
export const IntakeInvestmentTypeConfigSchema = z.object({
  /**
   * List of enabled investment types (at least one required)
   * Default: all types enabled
   */
  supportedInvestmentTypes: z
    .array(z.enum(['bridge', 'construction', 'rental', 'commercial']))
    .min(1, 'At least one investment type must be enabled'),
  /**
   * Whether to always show the "Not Sure - speak with advisor" option
   * Default: true
   */
  alwaysShowGeneral: z.boolean().default(true),
});

export type IntakeInvestmentTypeConfig = z.infer<typeof IntakeInvestmentTypeConfigSchema>;

/**
 * Default investment type configuration
 * All types enabled, general consultation always shown
 */
export const DEFAULT_INVESTMENT_TYPE_CONFIG: IntakeInvestmentTypeConfig = {
  supportedInvestmentTypes: ['bridge', 'construction', 'rental', 'commercial'],
  alwaysShowGeneral: true,
};

// =============================================================================
// UTILITY: CALCULATE RATE
// =============================================================================

import { type ConfigurableRateInput, calculateLoanRateWithConfig, type PricingRuleConfig } from './pricing-rules.dto';

/**
 * Options for rate calculation
 */
export interface RateCalculationOptions {
  /**
   * Optional configurable pricing rules. When provided, uses the configurable
   * rate calculator instead of the default hardcoded rules.
   * @see PricingRuleConfig from pricing-rules.dto.ts
   */
  pricingConfig?: PricingRuleConfig;

  /**
   * Additional inputs for configurable pricing (only used when pricingConfig is provided)
   */
  propertyType?: string;
  loanPurpose?: string;
  isRepeatBorrower?: boolean;
  hasCrossCollateral?: boolean;
  hasEarlyPayoffHistory?: boolean;
}

export function calculateLoanRate(
  input: RateCalculationInput,
  options?: RateCalculationOptions,
): RateCalculationResult {
  const {
    purchasePrice,
    rehabBudget,
    afterRepairValue,
    requestedLoanAmount,
    estimatedFico,
    preferredTerm,
    flipsInLast2Years,
    maxLTV,
    maxLTC,
    defaultRate,
  } = input;

  // If configurable pricing rules are provided, use the configurable calculator
  if (options?.pricingConfig) {
    const configurableInput: ConfigurableRateInput = {
      purchasePrice,
      rehabBudget,
      afterRepairValue,
      requestedLoanAmount,
      estimatedFico,
      preferredTerm,
      flipsInLast2Years,
      propertyType: options.propertyType,
      loanPurpose: options.loanPurpose,
      isRepeatBorrower: options.isRepeatBorrower,
      hasCrossCollateral: options.hasCrossCollateral,
      hasEarlyPayoffHistory: options.hasEarlyPayoffHistory,
    };

    const result = calculateLoanRateWithConfig(configurableInput, options.pricingConfig);

    // Map the configurable result to the standard result format
    return {
      estimatedRate: result.estimatedRate,
      ltv: result.ltv,
      ltc: result.ltc,
      monthlyPayment: result.monthlyPayment,
      totalCost: result.totalCost,
      isEligible: result.isEligible,
      ineligibilityReason: result.ineligibilityReason,
      adjustments: result.adjustments.map((adj) => ({
        description: `${adj.category}: ${adj.description}`,
        adjustment: adj.adjustment,
      })),
    };
  }

  // Default hardcoded calculation (backwards compatible)
  // Calculate LTV and LTC
  const totalCost = purchasePrice + rehabBudget;
  const ltv = (requestedLoanAmount / afterRepairValue) * 100;
  const ltc = (requestedLoanAmount / totalCost) * 100;

  // Check eligibility
  if (ltv > maxLTV) {
    return {
      estimatedRate: 0,
      ltv,
      ltc,
      monthlyPayment: 0,
      totalCost: 0,
      isEligible: false,
      ineligibilityReason: `LTV of ${ltv.toFixed(1)}% exceeds maximum of ${maxLTV}%`,
      adjustments: [],
    };
  }

  if (ltc > maxLTC) {
    return {
      estimatedRate: 0,
      ltv,
      ltc,
      monthlyPayment: 0,
      totalCost: 0,
      isEligible: false,
      ineligibilityReason: `LTC of ${ltc.toFixed(1)}% exceeds maximum of ${maxLTC}%`,
      adjustments: [],
    };
  }

  // Calculate rate adjustments
  const adjustments: Array<{ description: string; adjustment: number }> = [];
  let rate = defaultRate;

  // FICO adjustments
  if (estimatedFico === '760+') {
    adjustments.push({ description: 'Excellent credit (760+)', adjustment: -0.5 });
    rate -= 0.5;
  } else if (estimatedFico === '740-759') {
    adjustments.push({ description: 'Very good credit (740-759)', adjustment: -0.25 });
    rate -= 0.25;
  } else if (estimatedFico === '660-679' || estimatedFico === 'below-660') {
    adjustments.push({ description: 'Credit score adjustment', adjustment: 0.5 });
    rate += 0.5;
  }

  // Experience adjustments
  if (flipsInLast2Years === '11+') {
    adjustments.push({ description: 'Experienced investor (11+ deals)', adjustment: -0.5 });
    rate -= 0.5;
  } else if (flipsInLast2Years === '4-10') {
    adjustments.push({ description: 'Experienced investor (4-10 deals)', adjustment: -0.25 });
    rate -= 0.25;
  }

  // LTV adjustments
  if (ltv <= 60) {
    adjustments.push({ description: 'Low LTV (60% or below)', adjustment: -0.25 });
    rate -= 0.25;
  }

  // Calculate monthly payment (interest-only for short-term)
  const monthlyRate = rate / 100 / 12;
  const monthlyPayment = requestedLoanAmount * monthlyRate;

  // Calculate total cost over term
  const totalInterest = monthlyPayment * preferredTerm;
  const totalLoanCost = requestedLoanAmount + totalInterest;

  return {
    estimatedRate: Math.max(rate, 0),
    ltv,
    ltc,
    monthlyPayment,
    totalCost: totalLoanCost,
    isEligible: true,
    adjustments,
  };
}
